/**
 * LicensesPage - Dashboard de Licenças Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  TableContainer,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useAdministrator } from '../common/util/permissions';
import { useEffectAsync } from '../reactHelper';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const LicensesPage = () => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const admin = useAdministrator();

  const [server, setServer] = useState(null);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chart colors using premium tokens (series array)
  const chartColors = tokens?.chart?.series || [
    tokens?.accent?.primary || '#3B82F6',
    tokens?.semantic?.success || '#22C55E',
    tokens?.semantic?.warning || '#F59E0B',
    tokens?.semantic?.error || '#EF4444',
    tokens?.semantic?.purple || '#9333EA',
    tokens?.semantic?.info || '#06B6D4',
  ];

  useEffectAsync(async () => {
    try {
      const [serverResponse, usersResponse, devicesResponse] = await Promise.all([
        fetch('/api/server'),
        fetch('/api/users'),
        fetch('/api/devices?all=true'),
      ]);

      if (serverResponse.ok) {
        setServer(await serverResponse.json());
      }
      if (usersResponse.ok) {
        setUsers(await usersResponse.json());
      }
      if (devicesResponse.ok) {
        setDevices(await devicesResponse.json());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const stats = useMemo(() => {
    const total = server?.deviceLimit || 0;
    const used = devices.length;
    const available = total > 0 ? Math.max(0, total - used) : 0;
    const usagePercent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      total: total === -1 ? '∞' : total,
      used,
      available: total === -1 ? '∞' : available,
      usagePercent: total === -1 ? 0 : usagePercent,
      unlimited: total === -1,
    };
  }, [server, devices]);

  const devicesPerUser = useMemo(() => {
    const userMap = {};
    users.forEach((user) => {
      userMap[user.id] = {
        id: user.id,
        name: user.name,
        email: user.email,
        deviceCount: 0,
        deviceLimit: user.deviceLimit,
      };
    });

    devices.forEach((device) => {
      if (userMap[device.userId]) {
        userMap[device.userId].deviceCount += 1;
      }
    });

    return Object.values(userMap)
      .filter((user) => user.deviceCount > 0)
      .sort((a, b) => b.deviceCount - a.deviceCount);
  }, [users, devices]);

  const pieData = useMemo(() => {
    return devicesPerUser.slice(0, 6).map((user) => ({
      name: user.name,
      value: user.deviceCount,
    }));
  }, [devicesPerUser]);

  // Stats card styles (using hover as darker gradient end)
  const getCardStyle = (type) => {
    const gradients = {
      total: {
        background: `linear-gradient(135deg, ${tokens?.accent?.primary || '#3B82F6'} 0%, ${tokens?.accent?.hover || '#2563EB'} 100%)`,
        iconColor: 'rgba(255,255,255,0.9)',
      },
      used: {
        background: `linear-gradient(135deg, ${tokens?.semantic?.warning || '#F59E0B'} 0%, ${tokens?.semantic?.warningHover || '#D97706'} 100%)`,
        iconColor: 'rgba(255,255,255,0.9)',
      },
      available: {
        background: `linear-gradient(135deg, ${tokens?.semantic?.success || '#22C55E'} 0%, ${tokens?.semantic?.successHover || '#16A34A'} 100%)`,
        iconColor: 'rgba(255,255,255,0.9)',
      },
    };
    return gradients[type] || gradients.total;
  };

  // Header cell styles
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  if (!admin) {
    return (
      <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'licensesTitle']}>
        <Box sx={{ p: 3 }}>
          <Typography color="error">{t('errorGeneral')}</Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'licensesTitle']}>
      <Box sx={{ p: 2 }}>
        {loading ? (
          <LinearProgress sx={{ borderRadius: tokens?.radius?.sm }} />
        ) : (
          <>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: tokens?.radius?.lg || '12px',
                    ...getCardStyle('total'),
                    boxShadow: tokens?.shadows?.lg,
                  }}
                >
                  <CardContent sx={{ color: 'white' }}>
                    <CardMembershipIcon sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {t('licensesTotal')}
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.total}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: tokens?.radius?.lg || '12px',
                    ...getCardStyle('used'),
                    boxShadow: tokens?.shadows?.lg,
                  }}
                >
                  <CardContent sx={{ color: 'white' }}>
                    <DevicesIcon sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {t('licensesUsed')}
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.used}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: tokens?.radius?.lg || '12px',
                    ...getCardStyle('available'),
                    boxShadow: tokens?.shadows?.lg,
                  }}
                >
                  <CardContent sx={{ color: 'white' }}>
                    <CheckCircleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {t('licensesAvailable')}
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.available}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Usage Progress */}
            {!stats.unlimited && (
              <Paper
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: tokens?.text?.primary }}>
                    {t('licensesUsage')}
                  </Typography>
                  <Chip
                    icon={stats.usagePercent > 90 ? <WarningIcon /> : <CheckCircleIcon />}
                    label={`${stats.usagePercent}%`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: stats.usagePercent > 90 ? tokens?.semantic?.warningBg : tokens?.semantic?.successBg,
                      color: stats.usagePercent > 90 ? tokens?.semantic?.warning : tokens?.semantic?.success,
                      border: `1px solid ${stats.usagePercent > 90 ? `${tokens?.semantic?.warning}40` : `${tokens?.semantic?.success}40`}`,
                    }}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.usagePercent}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: tokens?.surface?.elevated,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      backgroundColor: stats.usagePercent > 90 ? tokens?.semantic?.warning : tokens?.accent?.primary,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, color: tokens?.text?.secondary }}>
                  {stats.used} / {stats.total} {t('deviceTitle').toLowerCase()}
                </Typography>
              </Paper>
            )}

            {/* Distribution Chart */}
            {pieData.length > 0 && (
              <Paper
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: tokens?.text?.primary }}>
                  {t('licensesDistribution')}
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill={chartColors[0]}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${entry.name}`} fill={chartColors[index % chartColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: tokens?.surface?.elevated,
                          border: `1px solid ${tokens?.border?.subtle}`,
                          borderRadius: tokens?.radius?.md,
                          color: tokens?.text?.primary,
                        }}
                      />
                      <Legend
                        wrapperStyle={{
                          color: tokens?.text?.secondary,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            )}

            {/* Devices per User Table */}
            <Paper
              sx={{
                overflow: 'hidden',
                borderRadius: tokens?.radius?.lg || '12px',
                backgroundColor: tokens?.surface?.glass,
                border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: tokens?.text?.primary }}>
                  {t('licensesPerUser')}
                </Typography>
              </Box>
              <TableContainer className="premium-table-container">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>{t('sharedName')}</TableCell>
                      <TableCell sx={headerCellSx}>{t('userEmail')}</TableCell>
                      <TableCell align="right" sx={headerCellSx}>{t('deviceTitle')}</TableCell>
                      <TableCell align="right" sx={headerCellSx}>{t('userDeviceLimit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devicesPerUser.map((user) => (
                      <TableRow
                        key={user.id}
                        hover
                        sx={{
                          transition: tokens?.transitions?.colors || 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: tokens?.text?.primary }}>
                            {user.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ color: tokens?.text?.secondary }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Chip
                            label={user.deviceCount}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: tokens?.semantic?.infoBg,
                              color: tokens?.semantic?.info,
                              border: `1px solid ${tokens?.semantic?.info}40`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ color: tokens?.text?.secondary }}>
                            {user.deviceLimit === -1 ? '∞' : user.deviceLimit || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {devicesPerUser.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: tokens?.text?.secondary }}>
                            {t('sharedNoData')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>
    </PageLayout>
  );
};

export default LicensesPage;
